import { useEffect, useState } from "react";
import { Loader2, Save, CheckCircle2, AlertTriangle, SearchCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const F = [
  ["seo_title", "SEO Title", "Shown as the Google result headline (max 70 chars)"],
  ["meta_description", "Meta Description", "Google result snippet (max 170 chars)"],
  ["keywords", "Keywords", "Comma-separated (optional)"],
  ["og_image", "OG Image URL", "Social sharing image (https)"],
];
const BIZ = [
  ["business_type", "Business Type", "e.g. Store, Restaurant, LocalBusiness"],
  ["address", "Address", ""], ["city", "City", ""], ["state", "State", ""], ["country", "Country", ""],
  ["hours", "Opening Hours", "e.g. Mo-Sa 10:00-20:00"],
  ["socials", "Social Profiles", "Comma-separated profile URLs"],
];
const TRACK = [
  ["pixel_id", "Meta Pixel ID", "Your own pixel — events stay in YOUR ad account"],
  ["ga4_id", "GA4 Measurement ID", "e.g. G-XXXXXXX"],
  ["gads_id", "Google Ads ID", "e.g. AW-XXXXXXX"],
  ["gads_label", "Google Ads Conversion Label", ""],
  ["gsc_verification", "Google Search Console Token", "The content= value of the verification meta tag"],
];

export default function StoreSeo() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/store-admin/seo").then((r) => {
    setData(r.data);
    const f = {};
    [...F, ...BIZ, ...TRACK].forEach(([k]) => { f[k] = r.data.seo?.[k] || ""; });
    setForm(f);
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/store-admin/seo", form);
      setData((d) => ({ ...d, readiness: r.data.readiness }));
      toast.success("SEO settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  const r = data.readiness;
  const title = form.seo_title || data.website?.name || "";
  const desc = form.meta_description || "";

  const Group = ({ title, fields, testid }) => (
    <div className="bg-card border border-border rounded-2xl p-6" data-testid={testid}>
      <p className="font-display font-bold text-base mb-4">{title}</p>
      <div className="space-y-3">
        {fields.map(([k, label, hint]) => (
          <div key={k}>
            <div className="flex items-center justify-between">
              <label className="text-xs font-extrabold uppercase tracking-widest text-muted-foreground">{label}</label>
              {(k === "seo_title" || k === "meta_description") && (
                <span className={`text-[10px] font-bold ${(form[k] || "").length > (k === "seo_title" ? 70 : 170) ? "text-red-500" : "text-muted-foreground"}`}>
                  {(form[k] || "").length}/{k === "seo_title" ? 70 : 170}
                </span>
              )}
            </div>
            <Input data-testid={`seo-${k}`} value={form[k] || ""} placeholder={hint}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 h-10" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="store-seo-page" className="max-w-5xl">
      <PageHeader title="SEO & Google" subtitle="Search metadata, local business data and tracking for YOUR store only." testid="seo-title" />

      <div className={`rounded-2xl border p-4 mb-6 flex items-center gap-3 ${r.status === "SEO READY" ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="seo-readiness">
        {r.status === "SEO READY" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <AlertTriangle className="h-5 w-5 text-amber-500" />}
        <p className="font-extrabold text-sm">{r.status}</p>
        <div className="flex flex-wrap gap-2 ml-auto">
          {r.checks.filter((c) => !c.ok).map((c) => (
            <span key={c.id} data-testid={`issue-${c.id}`} className="text-xs font-bold text-amber-600 bg-amber-500/10 rounded-full px-2.5 py-1">{c.detail}</span>
          ))}
          {r.checks.every((c) => c.ok) && <span className="text-xs font-bold text-emerald-600">All checks passed</span>}
        </div>
      </div>

      {/* Google preview */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="google-preview">
        <p className="text-xs uppercase tracking-widest font-extrabold text-muted-foreground mb-3">Google Search Preview</p>
        <div className="bg-white rounded-xl p-4 max-w-xl border border-black/5">
          <p className="text-xs text-zinc-600">https://miltroo.com/site/{data.website?.slug}</p>
          <p className="text-[#1a0dab] text-lg leading-snug mt-0.5 truncate" data-testid="preview-title">{title || "Your Store Name"}</p>
          <p className="text-sm text-zinc-600 mt-0.5 line-clamp-2" data-testid="preview-desc">{desc || "Your meta description appears here…"}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <Group title="Search Metadata" fields={F} testid="seo-metadata" />
        <Group title="Local Business Data (structured data)" fields={BIZ} testid="seo-business" />
      </div>
      <Group title="Tracking & Verification (your own accounts only)" fields={TRACK} testid="seo-tracking" />

      <Button data-testid="seo-save-button" onClick={save} disabled={busy} className="rounded-full font-bold h-11 px-8 mt-6">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save SEO Settings
      </Button>
      <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
        <SearchCheck className="h-3.5 w-3.5" /> Sitemap & robots.txt are auto-generated. After saving, request indexing in Google Search Console — Google may take time to crawl and index pages.
      </p>
    </div>
  );
}
